import React, {useState, useEffect, useContext} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import Divider from '../../components/elements/Divider';
import Popup from '../../components/elements/Popup';
import Text from '../../components/elements/Text';
import Input from '../../components/elements/Input';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {
  DLog,
  isEmpty,
  transformPhoneForAPI,
  validPassword,
} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {HTTP_METHODS, RESET_PASSWORD} from '../../utils/URLConstants';
import * as Keychain from 'react-native-keychain';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {tID} from '../../utils/TestIdHelper';
import {ClosedEyeIcon, OpenEyeIcon} from '../../utils/SvgIcons';

export const PasswordRecoverySuccessModal = ({
  showSuccesModal = false,
  onPressFinish = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('PasswordRecovery-Modal-Popup')}
      visible={showSuccesModal}
      textAlign="center"
      title="Success!"
      description={
        <>
          Your password has been changed successfully. You can now access your
          account in the{'\n'}
          <Text
            testID={tID('PasswordRecovery-Text-ChangeSuccess')}
            variation="Body1SemiBold"
            textAlign="center"
            color={colors.textMuted}>
            Diathrive Health™
          </Text>{' '}
          app.
        </>
      }>
      <Button
        testID={tID('PasswordRecovery-Button-Finish')}
        style={styles.button}
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
        onPress={onPressFinish}
        title={'Finish'}
      />
    </Popup>
  );
};

const PasswordRecovery = ({navigation, route, authCredentials}) => {
  //added prop for use in web only
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [showSuccesModal, setshowSuccesModal] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isValidForm, setValidForm] = useState(false);
  const [hasPasswordError, setHasPasswordError] = useState(false);
  const [hasRepeatPasswordError, setHasRepeatPasswordError] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    validatePasswordRecovery();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validator is re-created each render; validation is intentionally keyed to the password inputs only
  }, [password, repeatPassword]);

  const onPressShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onPressShowRepeatPassword = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const onPressFinish = () => {
    setshowSuccesModal(false);
    if (Platform.OS === 'web') {
      navigation('/login');
    } else navigation.navigate('SignIn');
  };

  const validatePasswordRecovery = () => {
    if (isEmpty(password) || isEmpty(repeatPassword)) {
      setValidForm(false);
      return;
    }

    setValidForm(true);
  };

  const onPressConfirm = async () => {
    if (!validPassword(password)) {
      setHasPasswordError(true);
      return;
    } else {
      setHasPasswordError(false);
    }

    if (password !== repeatPassword) {
      setHasRepeatPasswordError(true);
      return;
    } else {
      setHasRepeatPasswordError(false);
    }

    let credentials;
    if (Platform.OS === 'web') {
      credentials = authCredentials;
    } else {
      //fetch the user details from store
      credentials = await Keychain.getGenericPassword();
    }
    let email = credentials?.username;

    const postData = {
      phone:
        Platform.OS === 'web'
          ? transformPhoneForAPI(authCredentials.phone)
          : transformPhoneForAPI(route.params.phone),
      password: password,
      repeatPassword: repeatPassword,
      passwordRecoveryToken:
        Platform.OS === 'web' ? authCredentials.token : route.params.token,
      email,
    };
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.POST, RESET_PASSWORD, postData)
      .then(response => {
        DLog(response);
        lpc.setVisible(false);
        if (response.status == 200) {
          setTimeout(() => {
            setshowSuccesModal(true);
          }, 1000);
        } else {
        }
      })
      .catch(error => {
        DLog(error);
        lpc.setVisible(false);
        esc.showError(error);
        // if (error.data) {
        //   if (error.data.errors instanceof Array) {
        //     alert(error.data.errors[0].message);
        //   } else if (typeof error.data.errors === 'string') {
        //     alert(error.data.errors);
        //   }
        // }
      });
  };

  return (
    <SafeAreaView
      testID={tID('PasswordRecovery-SafeAreaView-Main')}
      style={styles.container}>
      {Platform.OS != 'web' && (
        <View
          testID={tID('PasswordRecovery-View-HeaderContainer')}
          style={[styles.headerView, {marginTop: 30}]}>
          <Header
            testID={tID('PasswordRecovery-Header-Recovery')}
            title="Password Recovery"
            hideLeftIem
          />
        </View>
      )}
      <KeyboardAvoidingView
        testID={tID('PasswordRecovery-KeyboardAvoidingView-1')}
        behavior={Platform.OS === 'ios' ? 'padding' : null}
        style={styles.container}
        // keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
      >
        <ScrollView keyboardShouldPersistTaps="handled"
          testID={tID('PasswordRecovery-ScrollView-1')}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollviewContainer}>
          {Platform.OS === 'web' ? (
            <Text testID={tID('PasswordRecovery-Text-Title')}>
              Password Recovery
            </Text>
          ) : (
            <Text
              testID={tID('PasswordRecovery-Text-Title')}
              style={styles.title}
              color={colors.textPrimary}>
              Create new password
            </Text>
          )}
          <Text
            testID={tID('PasswordRecovery-Text-SubTitle')}
            variation="Text1Regular"
            color={colors.textSecondary}>
            Create a new password to access your account. Password must be at
            least 8 characters and contain at least one capital letter, one
            special character, one number, and cannot be a common password.
          </Text>
          <Divider top={16} />

          <Input
            testID={tID('PasswordRecovery-Input-NewPassword')}
            hasError={hasPasswordError}
            errorText={'Password doesn’t meet the requirements.'}
            secureTextEntry={!showPassword}
            textContentType="newPassword"
            autoComplete="new-password"
            inputTitle={'New Password'}
            placeholder={'Type here...'}
            value={password}
            onChangeText={setPassword}
            topMargin={24}
            rightIcon={showPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
            onPressRightIcon={onPressShowPassword}
            style={Platform.OS === 'web' && styles.flex1Width292}
          />

          <Input
            testID={tID('PasswordRecovery-Input-RepeatPassword')}
            hasError={hasRepeatPasswordError}
            errorText={'Passwords do not match.'}
            secureTextEntry={!showRepeatPassword}
            textContentType="newPassword"
            autoComplete="new-password"
            inputTitle={'Repeat Password'}
            placeholder={'Type here...'}
            value={repeatPassword}
            onChangeText={setRepeatPassword}
            topMargin={24}
            rightIcon={showRepeatPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
            onPressRightIcon={onPressShowRepeatPassword}
            style={Platform.OS === 'web' && styles.flex1Width292}
          />
        </ScrollView>
      </KeyboardAvoidingView>
      <View style={[styles.buttonContainer, {paddingBottom: 20}]}>
        <Button
          testID={tID('PasswordRecovery-Button-Confirm')}
          disabled={!isValidForm}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          title={'Confirm New Password'}
          onPress={onPressConfirm}
          style={Platform.OS === 'web' && styles.flex1Width292}
        />
      </View>
      <PasswordRecoverySuccessModal
        showSuccesModal={showSuccesModal}
        onPressFinish={onPressFinish}
      />
    </SafeAreaView>
  );
};

export default PasswordRecovery;
